import React from 'react';
import EventFlyerMedia from '../../EventFlyerMedia';
import ChatSidePanelCard from './ChatSidePanelCard';
import ChatSidePanelInfoRow from './ChatSidePanelInfoRow';
import ChatSidePanelProgressBlock from './ChatSidePanelProgressBlock';
import { openExternalUrl } from '../../../../utils/externalLink';
import EventVenueDisplay, { buildVenueAddress, buildVenueMapUrl } from '../../events/EventVenueDisplay';

const noop = () => {};
const defaultResolveEventDetailPath = (eventUuid) => eventUuid ? '#' : '';
const defaultOnFlyerImageLoad = noop;

export default function EventInfoSidePanelCard({
  selectedEventInfo = null,
  selectedEventFlyerUrl = '',
  selectedEventFlyerBackUrl = '',
  selectedEventFlyerFullUrl = '',
  selectedEventFlyerBackFullUrl = '',
  onFlyerImageLoad = defaultOnFlyerImageLoad,
  isSelectedGroupUnified = false,
  groupEventDetailRows = [],
  resolveEventDetailPath = defaultResolveEventDetailPath,
  eventSchedule = {},
  scheduleDetail = {},
  scheduleValueClassName = 'side-info-val',
  venueLink = '',
  venueResolvedName = '',
  venueInfo = {},
  venueId = null,
  venueDetailBasePath = '',
  mapLinkUrl = '',
  venueResolvedAddress = '',
  sidebarNote = '',
  showGroupTaskProgress = false,
  showGroupCastingProgress = false,
  showGroupTicketProgress = false,
  showTaskProgressBlock = false,
  showCastingProgressBlock = false,
  showTicketProgressBlock = false,
  taskProgressPercent = 0,
  castingPercent = 0,
  ticketPercent = 0,
  isTicketSummaryLoading = false,
  eventDetailUrl = '',
}) {
  const safeGroupRows = Array.isArray(groupEventDetailRows) ? groupEventDetailRows : [];
  const multiPerformanceRows = Array.isArray(selectedEventInfo?.performances)
    ? selectedEventInfo.performances
    : [];
  const isMultiPerformancePanel =
    !isSelectedGroupUnified &&
    String(selectedEventInfo?.event_mode || selectedEventInfo?.eventMode || '').toLowerCase() === 'multi_performance' &&
    multiPerformanceRows.length > 0;
  const hasFrontFlyer = Boolean(selectedEventFlyerUrl);
  const hasBackFlyer = Boolean(selectedEventFlyerBackUrl);
  const hasAnyFlyer = hasFrontFlyer || hasBackFlyer;

  return (
    <ChatSidePanelCard title="">{/* 見出しはパネル上部バー「イベント情報」が兼ねるため省略（二重表示回避） */}

      {hasAnyFlyer ? (
        <div className="side-flyer-pair">
          {hasFrontFlyer ? (
            <button
              type="button"
              className="side-flyer-tile"
              onClick={() => openExternalUrl(selectedEventFlyerFullUrl)}
              title={selectedEventFlyerFullUrl ? 'フライヤー（表）を新しいタブで開く' : 'フライヤー（表）'}
              aria-label="フライヤー（表）を開く"
            >
              <EventFlyerMedia
                event={selectedEventInfo}
                imageUrl={selectedEventFlyerUrl}
                imageAlt="フライヤー表"
                sizeVariant="small"
                borderRadius={8}
                showPlaceholder={false}
                onImageLoad={(target) => onFlyerImageLoad(target)}
              />
            </button>
          ) : null}
          {hasBackFlyer ? (
            <button
              type="button"
              className="side-flyer-tile"
              onClick={() => openExternalUrl(selectedEventFlyerBackFullUrl)}
              title={selectedEventFlyerBackFullUrl ? 'フライヤー（裏）を新しいタブで開く' : 'フライヤー（裏）'}
              aria-label="フライヤー（裏）を開く"
            >
              <EventFlyerMedia
                event={selectedEventInfo}
                imageUrl={selectedEventFlyerBackUrl}
                imageAlt="フライヤー裏"
                sizeVariant="small"
                borderRadius={8}
                showPlaceholder={false}
                onImageLoad={(target) => onFlyerImageLoad(target)}
              />
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="side-info-grid">
        <div className="side-event-name">{selectedEventInfo?.name || 'イベント'}</div>
        {isSelectedGroupUnified ? (
          <div className="side-group-event-section">
            {safeGroupRows.length > 0 ? (
              <ul className="side-group-event-list">
                {safeGroupRows.map((entry) => (
                  <li key={entry?.key} className="side-group-event-item">
                    {entry?.eventUuid ? (
                      <a
                        href={resolveEventDetailPath(entry.eventUuid)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="side-group-event-name side-group-event-name--link"
                        title="公演詳細ページを開く"
                      >
                        {entry?.performance}
                      </a>
                    ) : (
                      <div className="side-group-event-name">{entry?.performance}</div>
                    )}
                    <div className="side-group-event-meta">日付: {entry?.date}</div>
                    <div className="side-group-event-meta">開場 / 開演: {entry?.open} / {entry?.start}</div>
                    <div className="side-group-event-meta side-group-event-meta--venue">
                      <span className="side-group-event-venue-label">会場: </span>
                      <EventVenueDisplay
                        venueName={entry?.venueName}
                        venueId={entry?.venueId}
                        venueDetailBasePath={venueDetailBasePath}
                        address={entry?.address}
                        mapUrl={entry?.mapLinkUrl}
                        fallback="会場未設定"
                        className="side-group-event-venue-line"
                        linkClassName="side-link side-group-event-venue-link"
                        target="_blank"
                      />
                    </div>
                    <div className="side-group-event-address">{entry?.address}</div>
                    {entry?.note ? (
                      <div className="side-group-event-note">{entry.note}</div>
                    ) : null}
                    {showGroupTaskProgress ? (
                      <ChatSidePanelProgressBlock
                        wrapperClassName="side-group-event-progress"
                        headerClassName="side-group-event-progress-item side-group-event-progress-labels"
                        labelClassName="side-group-event-progress-label"
                        iconClass="fa-solid fa-list-check event-card-progress-icon event-card-progress-icon--task"
                        label="Tasks"
                        valueClassName="event-card-progress-value event-card-progress-value--task"
                        fillClassName="event-card-progress-fill event-card-progress-fill--task"
                        percent={entry?.taskProgress?.percent ?? 0}
                      />
                    ) : null}
                    {showGroupCastingProgress ? (
                      <ChatSidePanelProgressBlock
                        wrapperClassName="side-group-event-progress"
                        headerClassName="side-group-event-progress-item side-group-event-progress-labels"
                        labelClassName="side-group-event-progress-label"
                        iconClass="fa-solid fa-user event-card-progress-icon event-card-progress-icon--casting"
                        label="Casting"
                        valueClassName="event-card-progress-value event-card-progress-value--casting"
                        fillClassName="event-card-progress-fill event-card-progress-fill--casting"
                        percent={entry?.castingProgress?.percent ?? 0}
                      />
                    ) : null}
                    {showGroupTicketProgress ? (
                      <ChatSidePanelProgressBlock
                        wrapperClassName="side-group-event-progress"
                        headerClassName="side-group-event-progress-item side-group-event-progress-labels"
                        labelClassName="side-group-event-progress-label"
                        iconClass="fa-solid fa-ticket event-card-progress-icon event-card-progress-icon--ticket"
                        label="Tickets"
                        valueClassName="event-card-progress-value event-card-progress-value--ticket"
                        fillClassName="event-card-progress-fill event-card-progress-fill--ticket"
                        percent={entry?.ticketProgress?.percent ?? 0}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="side-performance-empty">公演情報を取得できませんでした</span>
            )}
          </div>
        ) : isMultiPerformancePanel ? (
          <div className="side-group-event-section">
            <ul className="side-group-event-list">
              {multiPerformanceRows.map((performance, index) => {
                const venueName =
                  performance?.venue_name ||
                  performance?.venue_name_snapshot ||
                  performance?.venue ||
                  '会場未設定';
                const address = buildVenueAddress(performance);
                const displayAddress = address || '所在地未設定';
                const mapUrl = buildVenueMapUrl({
                  name: venueName,
                  address,
                  fallback: performance?.prefecture || performance?.venue_prefecture || '',
                });
                return (
                  <li
                    key={performance?.id || performance?.sort_order || index}
                    className="side-group-event-item"
                  >
                    <div className="side-group-event-name">
                      {performance?.performance_name || `公演${index + 1}`}
                    </div>
                    <div className="side-group-event-meta">
                      日付: {performance?.performance_date || '未設定'}
                    </div>
                    <div className="side-group-event-meta">
                      開場 / 開演{performance?.end_time ? ' / 終演' : ''}: {performance?.open_time || '未設定'} / {performance?.start_time || '未設定'}
                      {performance?.end_time ? ` / ${performance.end_time}` : ''}
                    </div>
                    <div className="side-group-event-meta side-group-event-meta--venue">
                      <span className="side-group-event-venue-label">会場: </span>
                      <EventVenueDisplay
                        venueName={venueName}
                        venueId={performance?.venue_id}
                        venueDetailBasePath={venueDetailBasePath}
                        address={address}
                        mapUrl={mapUrl}
                        fallback="会場未設定"
                        className="side-group-event-venue-line"
                        linkClassName="side-link side-group-event-venue-link"
                        target="_blank"
                      />
                    </div>
                    <div className="side-group-event-address">{displayAddress}</div>
                    {performance?.notes ? (
                      <div className="side-group-event-note">{performance.notes}</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : selectedEventInfo?.performance_type ? (
          <ChatSidePanelInfoRow
            label="公演"
            value={selectedEventInfo.performance_type}
          />
        ) : null}
        {!isSelectedGroupUnified && !isMultiPerformancePanel ? (
          <>
            <ChatSidePanelInfoRow
              label="日付"
              value={eventSchedule?.date || '未設定'}
            />
            <ChatSidePanelInfoRow
              label="開場 / 開演"
              value={(scheduleDetail?.open || '未設定') + ' / ' + (scheduleDetail?.start || '未設定')}
              valueClassName={scheduleValueClassName}
            />
            <ChatSidePanelInfoRow
              label="会場"
              value={(
                <EventVenueDisplay
                  venueName={venueResolvedName || venueInfo?.name}
                  venueId={venueId}
                  venueDetailBasePath={venueDetailBasePath}
                  address={venueResolvedAddress || venueInfo?.address}
                  mapUrl={mapLinkUrl}
                  fallback="会場未設定"
                  linkClassName="side-link"
                  target="_blank"
                />
              )}
              valueClassName="side-info-val side-info-val--venue"
            />
            <ChatSidePanelInfoRow
              label={(venueLink || venueResolvedAddress) ? '住所' : '都道府県'}
              value={venueResolvedAddress || venueInfo?.address || '所在地未設定'}
            />
            {sidebarNote ? (
              <ChatSidePanelInfoRow
                label="メモ"
                value={<span style={{ whiteSpace: 'pre-line' }}>{sidebarNote}</span>}
                valueClassName="side-info-val"
              />
            ) : null}
          </>
        ) : null}
      </div>

      {!isSelectedGroupUnified ? (
        <>
          {showTaskProgressBlock
            ? (
              <ChatSidePanelProgressBlock
                iconClass="fa-solid fa-list-check event-card-progress-icon event-card-progress-icon--task"
                label="Tasks"
                valueClassName="event-card-progress-value event-card-progress-value--task"
                fillClassName="event-card-progress-fill event-card-progress-fill--task"
                percent={taskProgressPercent}
              />
            )
            : null}
          {showCastingProgressBlock
            ? (
              <ChatSidePanelProgressBlock
                iconClass="fa-solid fa-user event-card-progress-icon event-card-progress-icon--casting"
                label="Casting"
                valueClassName="event-card-progress-value event-card-progress-value--casting"
                fillClassName="event-card-progress-fill event-card-progress-fill--casting"
                percent={castingPercent}
              />
            )
            : null}
          {showTicketProgressBlock
            ? (
              <ChatSidePanelProgressBlock
                iconClass="fa-solid fa-ticket event-card-progress-icon event-card-progress-icon--ticket"
                label="Tickets"
                valueClassName="event-card-progress-value event-card-progress-value--ticket"
                fillClassName="event-card-progress-fill event-card-progress-fill--ticket"
                percent={ticketPercent}
                loading={isTicketSummaryLoading}
              />
            )
            : null}
        </>
      ) : null}

      {eventDetailUrl ? (
        <a
          href={eventDetailUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="side-link side-link--primary"
        >
          イベント詳細ページを開く
        </a>
      ) : null}
    </ChatSidePanelCard>
  );
}
